import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { useFeatures } from "../../context/FeaturesContext";

/**
 * Wrap an admin/seller page to lock it behind a specific feature key.
 * If the feature is not enabled, render a contextual "upgrade" CTA instead
 * of the real page content.
 *
 *   <FeatureGate featureKey="social_reels">
 *     <AdminSocial />
 *   </FeatureGate>
 */
export default function FeatureGate({ featureKey, title, children }) {
  const { has, loading } = useFeatures();
  if (loading) {
    return <div className="h-48 bg-[hsl(var(--kb-surface-alt))] animate-pulse" data-testid="feature-gate-loading" />;
  }
  if (has(featureKey)) return children;
  return (
    <div className="max-w-2xl mx-auto py-10 text-center" data-testid={`feature-locked-${featureKey}`}>
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--kb-surface-alt))] mb-4">
        <Lock size={28} />
      </div>
      <h1 className="font-display text-3xl">{title || "This module is locked"}</h1>
      <p className="text-sm text-[hsl(var(--kb-muted))] mt-3">
        The <span className="font-mono">{featureKey}</span> add-on isn't part of your current plan. Enable it from Billing to start using it.
      </p>
      <Link to="/admin/billing" className="kb-btn-primary inline-flex items-center gap-2 mt-6" data-testid="feature-gate-upgrade">
        Go to Billing <ArrowRight size={14} />
      </Link>
    </div>
  );
}
